import type { KeyboardEvent } from 'react';

import { validatePositiveDecimalInput } from './input-validation';

/**
 * Checks if a string is a non-negative decimal representation with at most `maxDecimals` digits after '.'.
 * Examples (maxDecimals=5): `5`, `5.`, `5.12345`, but NOT `5.123456`, `-1`, `1a`.
 */
export const isNonNegativeDecimalWithMaxPlaces = (raw: string, maxDecimals: number) => {
  if (raw === '') return false;

  // Match UI rule: at least one digit before '.', and up to `maxDecimals` digits after '.'.
  // Allows in-progress values like `5.` (fractional digits length = 0).
  const pattern = new RegExp(`^\\d+(\\.\\d{0,${maxDecimals}})?$`);
  return pattern.test(raw);
};

/**
 * Truncates a non-negative decimal input string to `maxDecimals` fractional digits.
 * Keeps in-progress values like `5.` intact while typing.
 */
export const normalizeDecimalInputToMaxPlaces = (raw: string, maxDecimals: number) => {
  // Accept digits with optional one decimal point; keep in-progress values like `5.`.
  const m = raw.match(/^(\d+)(?:\.(\d*))?$/);
  if (!m) return raw;

  const intPart = m[1];
  const fracPart = m[2];

  if (fracPart === undefined) return intPart;

  const truncatedFrac = fracPart.length > maxDecimals ? fracPart.slice(0, maxDecimals) : fracPart;
  if (truncatedFrac === '') return `${intPart}.`;

  return `${intPart}.${truncatedFrac}`;
};

/**
 * Rounds a number to `maxDecimals` fractional digits, then trims trailing zeros.
 */
export const formatNumberToMaxPlaces = (value: number, maxDecimals: number) => {
  const factor = 10 ** maxDecimals;
  const rounded = Math.round(value * factor) / factor;
  // Always compute with fixed precision then trim trailing zeros.
  return rounded.toFixed(maxDecimals).replace(new RegExp(`\\.?0+$`), '');
};

export const getMaxDecimalPlacesPattern = (maxDecimals: number) => `^\\d+(\\.\\d{0,${maxDecimals}})?$`;

/**
 * Blocks additional digits once the user tries to exceed `maxDecimals` fractional places.
 * This is optimized for `Field.Text` typing UX (keeps in-progress decimals like `5.` valid).
 */
export const validatePositiveDecimalMaxPlacesInput = (
  e: KeyboardEvent<HTMLElement>,
  maxDecimals: number,
) => {
  // `TextField`'s `onKeyPress` typing is not always `HTMLInputElement` depending on where it's wired.
  // We only rely on `.target.value` / selection offsets, so we safely cast.
  validatePositiveDecimalInput(e as KeyboardEvent<HTMLInputElement>);

  const target = e.target as HTMLInputElement;
  const key = e.key;

  // Only constrain digits after the decimal.
  if (!/^\d$/.test(key)) return;

  const value = target.value;
  const dotIndex = value.indexOf('.');
  if (dotIndex === -1) return;

  const selStart = target.selectionStart ?? value.length;
  const selEnd = target.selectionEnd ?? selStart;

  // If inserting/replacing before '.', allow it.
  if (selStart <= dotIndex) return;

  const fractionLeft = value.slice(dotIndex + 1, selStart);
  const fractionRight = value.slice(selEnd);
  const newFractionLength = fractionLeft.length + 1 + fractionRight.length;

  if (newFractionLength > maxDecimals) {
    e.preventDefault();
  }
};

